import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  TableBody,
  TableContainer,
} from "@material-ui/core";
import moment from "moment";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMergeBookings } from "@store/actions/bookingActions";
import { useNotificationContext } from "@providers/NotificationProvider";

function BookingMergeModal({ open, setOpen, selectedBooking, bookings }) {
  const [from, setFrom] = useState([]);
  const [to, setTo] = useState({});
  const [all, setAll] = useState(false);
  const { showNotification } = useNotificationContext();
  const [mergeBookings] = useMergeBookings();
  const [merging, setMerging] = useState(false);
  const mergePending = useRef(false);

  useEffect(() => {
    setTo(selectedBooking);
  }, [selectedBooking]);

  useEffect(() => {
    if (all) {
      setFrom([...bookings.filter((b) => to.id !== b.id)]);
    } else {
      setFrom([]);
    }
  }, [all, to]);

  const isChecked = useCallback(
    (booking) => {
      return from.map((f) => f.id).indexOf(booking.id) >= 0;
    },
    [from]
  );

  const onCheck = (booking) => {
    if (isChecked(booking)) {
      setFrom(from.filter((f) => f.id !== booking.id));
    } else {
      setFrom([...from, booking]);
    }
  };

  const handleClose = () => {
    if (mergePending.current) return;
    setOpen(false);
  };

  const merge = async () => {
    if (mergePending.current) return;
    mergePending.current = true;
    setMerging(true);
    try {
      const result = await mergeBookings({
        variables: {
          from: (from || []).filter((b) => b.id !== to.id).map((b) => b.id),
          to: to.id,
        },
      });
      if (result.errors?.length || result.data?.mergeBookings?.id !== to.id) {
        showNotification("These bookings could not be merged. Please try again.");
        return;
      }
      showNotification(`${to.reference} Merged`);
      setOpen(false);
    } catch (error) {
      showNotification("These bookings could not be merged. Please try again.");
    } finally {
      mergePending.current = false;
      setMerging(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      disableBackdropClick={true}
    >
      <DialogTitle>Merge Bookings</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Combine the selected enquiries into the destination booking. Their
          boats and messages will be kept together.
        </Typography>
        <Grid container spacing={2} alignItems="center" justify="space-between">
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={8}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={merging}
                        checked={all}
                        onChange={() => setAll(!all)}
                      ></Checkbox>
                    </TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Enquiry date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id} value={booking}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          disabled={merging || booking.id === to.id}
                          checked={isChecked(booking)}
                          onChange={() => onCheck(booking)}
                        ></Checkbox>
                      </TableCell>
                      <TableCell>{booking.reference}</TableCell>
                      <TableCell>
                        {moment(booking.createdAt).format("ll")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <ChevronRightIcon />
          <Grid item xs={3}>
            <TextField
              fullWidth
              variant="outlined"
              select
              label="Merge into"
              size="small"
              value={to}
              disabled={merging}
              onChange={(e) => setTo(e.target.value)}
            >
              {bookings.map((booking) => (
                <MenuItem key={booking.id} value={booking}>
                  {booking.reference}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={merging}>
          Cancel
        </Button>
        <Button onClick={merge} color="primary" disabled={merging || !to.id || !from.some((booking) => booking.id !== to.id)}>
          {merging ? "Merging..." : "Merge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BookingMergeModal;
